import { useEffect, useMemo, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { AdminProject } from "../../../hooks/useGetAdminProjects";
import "./ProjectMapView.scss";

const makeIcon = (color: string, count: number) =>
  L.divIcon({
    className: "pmv-pin",
    html: `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="36" viewBox="0 0 28 36">
      <path d="M14 0C6.268 0 0 6.268 0 14c0 9.333 14 22 14 22S28 23.333 28 14C28 6.268 21.732 0 14 0z" fill="${color}" stroke="#fff" stroke-width="1.5"/>
      <circle cx="14" cy="14" r="6" fill="#fff"/>
    </svg>${count > 1 ? `<span class="pmv-count">${count}</span>` : ""}`,
    iconSize: [28, 36],
    iconAnchor: [14, 36],
    popupAnchor: [0, -38],
  });

const STATUS_COLOR: Record<string, string> = {
  Active: "#14508c",
  "Needs review": "#d97706",
  "Draw Request": "#7c3aed",
  "Pre-construction": "#0891b2",
  Completed: "#16a34a",
  Inactive: "#6b7280",
};

const DEFAULT_COLOR = "#6b7280";

interface ProjectMapViewProps {
  projects: AdminProject[];
  loading?: boolean;
  onProjectClick?: (project: AdminProject) => void;
}

export default function ProjectMapView({
  projects,
  loading,
  onProjectClick,
}: ProjectMapViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);

  const grouped = useMemo(() => {
    const map = new Map<string, AdminProject[]>();
    projects
      .filter((p) => p.coordinates)
      .forEach((p) => {
        const key = `${p.coordinates!.lat},${p.coordinates!.lon}`;
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(p);
      });
    return map;
  }, [projects]);

  const onClickRef = useRef(onProjectClick);
  useEffect(() => {
    onClickRef.current = onProjectClick;
  }, [onProjectClick]);

  useEffect(() => {
    if (!containerRef.current) return;

    const map = L.map(containerRef.current, {
      center: [39.5, -98.35],
      zoom: 4,
      zoomControl: true,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    map.eachLayer((layer) => {
      if (layer instanceof L.Marker) map.removeLayer(layer);
    });

    if (grouped.size === 0) return;

    const bounds: [number, number][] = [];

    grouped.forEach((group) => {
      const { lat, lon } = group[0].coordinates!;
      const dominantColor =
        STATUS_COLOR[group[0].status] ?? DEFAULT_COLOR;
      const marker = L.marker([lat, lon], {
        icon: makeIcon(dominantColor, group.length),
      }).addTo(map);

      const popupRows = group
        .map((project) => {
          const color = STATUS_COLOR[project.status] ?? DEFAULT_COLOR;
          return `<div class="mp-card">
            <div class="mp-name">${project.name}</div>
            <div class="mp-meta">
              <span class="mp-badge" style="background:${color}20;color:${color}">${project.status}</span>
              <span class="mp-type">${project.type}</span>
            </div>
            ${project.address ? `<div class="mp-addr">${project.address}</div>` : ""}
            ${project.bank ? `<div class="mp-bank">${project.bank}</div>` : ""}
            <div class="mp-progress">
              <div class="mp-bar"><div class="mp-fill" style="width:${project.progress}%;background:${color}"></div></div>
              <span>${Math.round(project.progress)}%</span>
            </div>
            <button class="mp-btn" data-id="${project.id}">View / Edit</button>
          </div>`;
        })
        .join('<div class="mp-divider"></div>');

      marker.bindPopup(`<div class="map-popup">${popupRows}</div>`, {
        maxWidth: 280,
        maxHeight: 400,
      });

      marker.on("popupopen", () => {
        group.forEach((project) => {
          const btn = document.querySelector<HTMLButtonElement>(
            `.mp-btn[data-id="${project.id}"]`,
          );
          btn?.addEventListener("click", () =>
            onClickRef.current?.(project),
          );
        });
      });

      bounds.push([lat, lon]);
    });

    if (bounds.length === 1) {
      map.setView(bounds[0], 12);
    } else {
      map.fitBounds(bounds as L.LatLngBoundsLiteral, { padding: [48, 48] });
    }
  }, [grouped]);

  return (
    <div className="projectMapView">
      {loading && <div className="pmv-banner">Loading your projects…</div>}
      <div className="pmv-legend">
        {Object.entries(STATUS_COLOR).map(([label, color]) => (
          <span key={label} className="pmv-legend-item">
            <span className="pmv-dot" style={{ background: color }} />
            {label}
          </span>
        ))}
      </div>
      <div ref={containerRef} className="pmv-map" />
    </div>
  );
}
